/**
 * Grava numa etiqueta NFC exatamente o mesmo link do QR desta mesa.
 *
 * Regra: o NFC NUNCA gera um link novo — recebe o `url` já usado pelo QR
 * (/m/{slug}?table={table.id}). Os QR impressos continuam válidos e não
 * precisam de ser reimpressos.
 *
 * Web NFC só existe em Chrome/Android sobre HTTPS. Onde não existe, o
 * botão explica e copia o link para gravar com a app de NFC do tablet.
 */
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Smartphone } from "lucide-react";

export function TableNfcWriteButton({ url, label = "GRABAR NFC" }) {
  const [writing, setWriting] = useState(false);
  const supported = typeof window !== "undefined" && "NDEFReader" in window;

  const writeTag = async () => {
    if (!url) return;
    if (!supported) {
      try {
        await navigator.clipboard.writeText(url);
        toast.info("Este dispositivo no permite grabar NFC. Link copiado — grábalo con la app NFC del móvil.");
      } catch {
        toast.info("Este dispositivo no permite grabar NFC. Usa el link del QR para grabar la etiqueta.");
      }
      return;
    }
    setWriting(true);
    try {
      const reader = new window.NDEFReader();
      await reader.write({ records: [{ recordType: "url", data: url }] });
      toast.success("Etiqueta NFC grabada con el link de esta mesa.");
    } catch (err) {
      toast.error(err?.message || "No se pudo grabar la etiqueta NFC.");
    }
    setWriting(false);
  };

  return (
    <Button
      variant="outline"
      size="sm"
      className="text-xs gap-1.5"
      onClick={writeTag}
      disabled={writing || !url}
      data-testid="write-nfc-btn"
    >
      <Smartphone className="w-3.5 h-3.5" /> {writing ? "ACERCA LA ETIQUETA…" : label}
    </Button>
  );
}
